import { useEffect, useState, useSyncExternalStore } from "react";
import { pairingStart } from "../account";
import { noteSessionEnded } from "../analytics";
import { forgetGuestComputer } from "../identity";
import type { Nav } from "../nav";
import { FolderIcon, LIVE_WITHIN_MS, SessionRow, sessionTitle } from "../session-row";
import { PagedList, RowSkeletons } from "../paged-list";
import { agentOf } from "@foreman/app-core";
import { STALE_AFTER_MS, sessionActivity } from "@foreman/protocol";
import { AskCard } from "../ask-card";
// import { TeamsNeedCard } from "../team-need"; // Teams hidden for now
import { ARCHIVE_OPTIONS, isArchived, useArchiveDays } from "../archive";
const ARCHIVE_LABEL = (days: number) => ARCHIVE_OPTIONS.find((o) => o.days === days)?.label ?? `${days} days`;
import { isAsk, useFeed, useSessionRows, type FeedRow } from "../sessions-feed";
import { groupByFolder, setHomeView, useHomeView } from "../home-view";
import { tailPath } from "@foreman/app-core";
import { AndroidAppCard } from "../android-app";
import { CheckNowButton, ChevronIcon, ComputerIcon, Mascot, PlusIcon, STATUS, Sheet, TabBar, useApp, useHubSummaries } from "../ui";

const CARD_SHOWN = 5; // sessions a computer card shows before "Show more", so many sessions never turn Home into one giant list

/**
 * The first screen: every session on every computer in one list, what needs you at the top. Tapping a row goes straight to
 * the terminal (or to the inbox when the agent is asking something), without picking a computer first.
 */
export function HomeScreen({ nav }: { nav: Nav }) {
  const { store, hub, toast } = useApp();
  const summaries = useHubSummaries(hub);
  const feed = useFeed();
  const { needs, running, ended } = useSessionRows(feed);
  const asks = useSyncExternalStore(hub.subscribe, hub.getInbox).filter((i) => isAsk(i.event));
  const archiveDays = useArchiveDays();
  const view = useHomeView();
  const [unfolded, setUnfolded] = useState<Record<string, boolean>>({});
  const [pick, setPick] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  const [showIdle, setShowIdle] = useState(false);
  const [confirmStale, setConfirmStale] = useState(false);
  const [picking, setPicking] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const active = needs.length + running.length;

  const open = (r: FeedRow) => nav.push(r.ask ? { name: "inbox" } : { name: "terminal", computerId: r.computer.id, sessionId: r.session.id });
  const several = summaries.length > 1;
  const row = (r: FeedRow, onRemove?: () => void, hideDir?: boolean) => (
    <SessionRow key={`${r.computer.id}:${r.session.id}`} session={r.session} ask={r.ask} computerLabel={several && query.trim() ? r.computer.label : undefined} now={now} onOpen={() => open(r)} onRemove={onRemove} hideDir={hideDir} />
  );
  const refresh = () => {
    for (const s of summaries) if (s.state === "ready") hub.get(s.computer.id)?.refreshSessions();
    toast("Refreshing");
  };
  const newSession = () => {
    const ready = summaries.filter((s) => s.state === "ready");
    const chosen = ready.find((s) => s.computer.id === selectedId);
    if (chosen) return nav.push({ name: "sessions", computerId: chosen.computer.id, newSession: true });
    if (summaries.length === 1) return nav.push({ name: "sessions", computerId: summaries[0]!.computer.id, newSession: true });
    if (ready.length === 1) return nav.push({ name: "sessions", computerId: ready[0]!.computer.id, newSession: true });
    setPicking(true);
  };

  // A running session nobody has used for over a day (and that is not asking anything) folds away under "Idle".
  const isIdle = (r: FeedRow) => !r.ask && sessionActivity(r.session, now) === "dormant";
  // Everything that needs you is in the strip at the top, so the list below is the sessions that are working (or done).
  // Quiet for longer than the chosen time: out of the main lists, into "Archived" at the bottom
  const archived = (r: FeedRow) => isArchived(r.session, now, archiveDays);
  const liveRows = running.filter((r) => !archived(r));
  const idleRows = liveRows.filter(isIdle);
  const staleRows = idleRows.filter((r) => now - (r.session.lastActivityAt ?? now) > STALE_AFTER_MS);
  const endStale = () => {
    for (const r of staleRows) hub.get(r.computer.id)?.endSession(r.session.id);
    if (staleRows.length > 0) noteSessionEnded();
    setConfirmStale(false);
  };
  const countOn = (id: string) => needs.concat(running).filter((r) => r.computer.id === id && !archived(r)).length;
  // the computer whose sessions are listed: the one you tapped, else the busiest
  const selectedId = summaries.some((x) => x.computer.id === pick) ? pick : ([...summaries].sort((a, b) => countOn(b.computer.id) - countOn(a.computer.id))[0]?.computer.id ?? null);
  const q = query.trim().toLowerCase();
  const matches = (r: FeedRow) => !q || [sessionTitle(r.session), agentOf(r.session.command).label, r.session.cwd, r.computer.label].join(" ").toLowerCase().includes(q);
  const searchable = summaries.length > 0;
  const recent = (r: FeedRow) => r.session.lastActivityAt ?? r.session.startedAt;
  const shownLive = [...needs.filter((r) => !archived(r)), ...liveRows.filter((r) => !isIdle(r))].filter(matches).sort((a, b) => recent(b) - recent(a)); // the one you touched last is on top; what needs you is marked in its row
  const idleShown = idleRows.filter(matches);
  const doneAll = ended.filter((r) => !archived(r) && matches(r));
  const archivedAll = [...running, ...ended].filter((r) => archived(r) && matches(r)).sort((a, b) => (b.session.lastActivityAt ?? b.session.startedAt) - (a.session.lastActivityAt ?? a.session.startedAt));
  // an ended session can be forgotten: its conversation stays in Earlier messages, only the entry on the computer's list goes
  const removeRow = (r: FeedRow) => {
    if (r.session.status === "running") return undefined;
    return () => {
      if (hub.get(r.computer.id)?.removeSession(r.session.id)) toast("Removed");
    };
  };
  const list = (rows: FeedRow[], removable = false, id?: string) => <PagedList id={id} rows={rows} render={(r) => row(r, removable ? removeRow(r) : undefined)} />;
  const doneOpen = showDone || (!!q && doneAll.length > 0);
  const archivedOpen = showArchived || (!!q && archivedAll.length > 0);
  const idleOpen = showIdle || (!!q && idleShown.length > 0);
  const noMatches = !!q && shownLive.length + idleShown.length + doneAll.length + archivedAll.length === 0;
  const working = running.filter((r) => r.session.lastActivityAt !== undefined && now - r.session.lastActivityAt < LIVE_WITHIN_MS).length;
  const subtitle =
    summaries.length === 0
      ? "No computers yet"
      : [`${summaries.length} ${summaries.length === 1 ? "computer" : "computers"}`, needs.length > 0 && `${needs.length} ${needs.length === 1 ? "needs" : "need"} you`, active > 0 ? `${active} running` : "nothing running"].filter(Boolean).join(" · ");
  const tone = (r: FeedRow): "wait" | "idle" | "run" => (r.ask ? "wait" : sessionActivity(r.session, now) !== "active" ? "idle" : "run");

  return (
    <>
      <div className="screen" data-screen="home">
        <a className="skip-link" href="#home-main" onClick={(e) => (e.preventDefault(), document.getElementById("home-main")?.focus())}>
          Skip to sessions
        </a>
        <div className="safe-top">
          <div className="topbar home">
            <div className="title-col">
              <h1 className="t">Sessions</h1>
              <div className="sub" role="status" aria-live="polite">
                {subtitle}
              </div>
            </div>
            {searchable && (
              <button className={"icon-btn sq" + (searchOpen || q ? " on" : "")} aria-label="Search sessions" aria-pressed={searchOpen} onClick={() => setSearchOpen((v) => !v)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
              </button>
            )}
            {summaries.length > 0 && (
              <button className="icon-btn sq refresh-btn" aria-label="Refresh sessions" onClick={refresh} data-testid="refresh">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {summaries.length > 0 && (
          <button className="fab" aria-label="New session" onClick={newSession} data-testid="new-session">
            <PlusIcon />
          </button>
        )}

        <AndroidAppCard />

        {summaries.length === 0 ? (
          <div className="scroll">
            <div className="content">
              <div className="welcome">
                <Mascot size={104} front />
                <div className="welcome-name">Brocoded</div>
                <div className="big">Pair your first computer</div>
                <div className="copy">Open the Brocoded app on your computer: its window shows a QR code. Sign in here with Google, then scan it. That’s all.</div>
                <button className="btn primary" style={{ marginTop: 8 }} onClick={() => nav.push(pairingStart(store.list().length))}>
                  Pair a computer
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <main className="scroll" id="home-main" tabIndex={-1} aria-label="Sessions">
              <div className="content feed home-main-pad">
                {searchable && (searchOpen || q) && (
                  <div className="home-search" role="search">
                    <input className="input" type="search" enterKeyHint="search" placeholder="Search sessions" aria-label="Search sessions by title, agent, folder or computer" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
                  </div>
                )}
                <div className="view-toggle-row">
                  <div className="mode-toggle seg wide" role="group" aria-label="How to list sessions">
                    {(["flat", "folders"] as const).map((v) => (
                      <button key={v} className={"seg-item" + (view === v ? " on" : "")} aria-pressed={view === v} onClick={() => setHomeView(v)} data-testid={`view-${v}`}>
                        {v === "folders" ? "Folders" : "All sessions"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="comp-chips" role="group" aria-label="Computers">
                  {summaries.map((c) => {
                    const on = c.computer.id === selectedId;
                    return (
                      <button key={c.computer.id} className={"comp-chip" + (on ? " on" : "")} aria-pressed={on} data-state={c.state} onClick={() => setPick(c.computer.id)}>
                        <span className="comp-ico" aria-hidden>
                          <ComputerIcon />
                        </span>
                        <span className="comp-text">
                          <span className="comp-name">{c.computer.label}</span>
                          <span className="comp-state">
                            <span className="dot sm" style={{ background: c.state === "unauthorized" ? "var(--danger)" : STATUS[c.state].color }} aria-hidden />
                            {c.state === "unauthorized" ? "Removed" : STATUS[c.state].text}
                          </span>
                        </span>
                        <span className="comp-count">{countOn(c.computer.id)}</span>
                      </button>
                    );
                  })}
                </div>
                {/* Teams hidden for now: <TeamsNeedCard nav={nav} /> */}
                {asks.length > 0 && (
                  <section className="asks-strip" aria-label="Waiting for you">
                    <div className="home-kicker">
                      Needs you · {asks.length}
                      {asks.length > 1 ? " · swipe" : ""}
                    </div>
                    <div className="asks-rail" role="list">
                      {asks.map((a) => (
                        <div className="asks-slide" role="listitem" key={a.event.id}>
                          <AskCard item={a} nav={nav} online={summaries.find((s) => s.computer.id === a.computer.id)?.state === "ready"} />
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {summaries
                  .filter((c) => (q ? true : c.computer.id === selectedId))
                  .map((s) => {
                    const rows = shownLive.filter((r) => r.computer.id === s.computer.id);
                    const byFolder = view === "folders";
                    const groups = byFolder ? groupByFolder(rows) : [];
                    if (q && rows.length === 0) return null;
                    const f = feed.find((x) => x.computer.id === s.computer.id);
                    const cardLoading = !!f && f.snap.sessionsStatus === "loading" && f.snap.sessions.length === 0 && (f.state === "connecting" || f.state === "ready");
                    const isOpen = !!expanded[s.computer.id] || !!q;
                    const listId = `ccard-list-${s.computer.id}`;
                    const ready = s.state === "ready";
                    return (
                      <section className="home-comp" key={s.computer.id} aria-label={s.computer.label} data-state={s.state} aria-busy={cardLoading}>
                        {(q || !ready) && (
                          <div className="home-comp-head">
                            <span className="home-comp-name">{s.computer.label}</span>
                            <span className="ccard-state">{s.state === "unauthorized" ? "removed this phone" : STATUS[s.state].text}</span>
                            {s.state === "offline" && <CheckNowButton computerId={s.computer.id} />}
                          </div>
                        )}
                        {s.state === "unauthorized" && (
                          <div className="removed-card" role="alert" data-removed>
                            <div className="t">{s.computer.label} removed this phone</div>
                            <p className="fine">It can't connect any more. Pair again to get back in, or remove it from this list.</p>
                            <div className="removed-actions">
                              <button className="btn primary sm" onClick={() => nav.push(pairingStart(store.list().length))}>
                                Pair again
                              </button>
                              <button
                                className="text-btn plain"
                                onClick={() => {
                                  forgetGuestComputer(s.computer.daemonPublicKey);
                                  store.remove(s.computer.id);
                                  toast("Computer removed");
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )}
                        {cardLoading ? (
                          <RowSkeletons n={2} />
                        ) : rows.length > 0 ? (
                          byFolder ? (
                            groups.map((g) => {
                              const fk = `${s.computer.id}|${g.key}`;
                              const open = !!unfolded[fk] || !!q || groups.length === 1;
                              const gid = `${listId}-${groups.indexOf(g)}`;
                              const tones = g.rows.map(tone);
                              const n = (t: string) => tones.filter((x) => x === t).length;
                              const name = g.folder.replace(/[\\/]+$/, "").split(/[\\/]/).pop() || g.folder;
                              return (
                                <div className={"fcard" + (open ? " open" : "")} key={g.key}>
                                  <button className="fcard-head" aria-expanded={open} aria-controls={gid} onClick={() => setUnfolded((u) => ({ ...u, [fk]: !open }))}>
                                    <span className="fcard-ico" aria-hidden>
                                      <FolderIcon size={22} />
                                    </span>
                                    <span className="fcard-text">
                                      <span className="fcard-name">{name}</span>
                                      <span className="fcard-path" title={g.folder}>{tailPath(g.folder, 34)}</span>
                                    </span>
                                    <span className="fcard-count">{g.rows.length}</span>
                                    <span className="fcard-car" aria-hidden>
                                      <ChevronIcon />
                                    </span>
                                  </button>
                                  <div className="fcard-stats">
                                    {n("run") > 0 && <span className="stat run">{n("run")} running</span>}
                                    {n("wait") > 0 && <span className="stat wait">{n("wait")} waiting</span>}
                                    {n("idle") > 0 && <span className="stat idle">{n("idle")} idle</span>}
                                  </div>
                                  {open && (
                                    <div className="fcard-list">
                                      <PagedList id={gid} rows={g.rows} render={(r) => row(r, undefined, true)} />
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="fcard open flat">
                              {isOpen ? list(rows, false, listId) : <div className="list" id={listId}>{rows.slice(0, CARD_SHOWN).map((r) => row(r))}</div>}
                              {!q && rows.length > CARD_SHOWN && (
                                <button className="more-btn card-more" aria-expanded={isOpen} aria-controls={listId} onClick={() => setExpanded((e) => ({ ...e, [s.computer.id]: !isOpen }))}>
                                  {isOpen ? "Show fewer" : `Show ${rows.length - CARD_SHOWN} more`}
                                </button>
                              )}
                            </div>
                          )
                        ) : ready ? (
                          <div className="ccard-empty">No sessions running</div>
                        ) : null}
                      </section>
                    );
                  })}
                {noMatches && (
                  <div className="centered empty-state" role="status">
                    <div className="big">No matches</div>
                    <div className="copy">Nothing matches “{query.trim()}”.</div>
                  </div>
                )}
                {idleShown.length > 0 && (
                  <>
                    <button className="more-btn" aria-expanded={idleOpen} aria-controls="home-idle" onClick={() => setShowIdle((v) => !v)}>
                      {idleOpen ? "Hide" : "Show"} {idleShown.length} idle for over a day
                    </button>
                    {idleOpen && list(idleShown, false, "home-idle")}
                    {idleOpen && !q && staleRows.length > 0 && (
                      <button className="more-btn" onClick={() => setConfirmStale(true)}>
                        End {staleRows.length} untouched for over a week
                      </button>
                    )}
                  </>
                )}
                <button className="more-btn done-toggle" aria-expanded={doneOpen} aria-controls="home-done" onClick={() => setShowDone((v) => !v)} data-testid="done-toggle">
                  Done · {doneAll.length}
                  <span aria-hidden> {doneOpen ? "⌃" : "›"}</span>
                </button>
                {doneOpen && (
                  <>
                    {doneAll.length === 0 ? (
                      <div className="centered empty-state">
                        <div className="big">Nothing finished yet</div>
                        <div className="copy">Sessions that end stay here so you can read back what happened.</div>
                      </div>
                    ) : (
                      list(doneAll, true, "home-done")
                    )}
                  </>
                )}
                {archivedAll.length > 0 && (
                  <>
                    <button className="more-btn done-toggle" aria-expanded={archivedOpen} aria-controls="home-archived" onClick={() => setShowArchived((v) => !v)} data-testid="archived-toggle">
                      Archived · {archivedAll.length}
                      <span aria-hidden> {archivedOpen ? "⌃" : "›"}</span>
                    </button>
                    {archivedOpen && (
                      <>
                        <p className="fine" style={{ textAlign: "left", margin: "2px var(--gutter) 6px" }}>
                          Quiet for over {ARCHIVE_LABEL(archiveDays)}. Open one to look back at it, or remove the ones that have ended.
                        </p>
                        {list(archivedAll, true, "home-archived")}
                      </>
                    )}
                  </>
                )}
              </div>
            </main>
          </>
        )}
        <TabBar nav={nav} />
      </div>

      {confirmStale && (
        <Sheet onClose={() => setConfirmStale(false)}>
          <div className="head">
            <span className="t">End {staleRows.length} {staleRows.length === 1 ? "session" : "sessions"}?</span>
            <button className="text-btn plain" onClick={() => setConfirmStale(false)}>
              Cancel
            </button>
          </div>
          <div className="sheet-body">
            <p className="fine" style={{ textAlign: "left" }}>
              These haven't been used for over a week. Ending them stops the programs. Claude Code and Codex conversations stay in Earlier, so you can pick one up again with Resume.
            </p>
            <button className="btn primary" onClick={endStale}>
              End {staleRows.length}
            </button>
          </div>
        </Sheet>
      )}

      {picking && (
        <Sheet onClose={() => setPicking(false)}>
          <div className="head">
            <span className="t">New session on…</span>
            <button className="text-btn plain" onClick={() => setPicking(false)}>
              Cancel
            </button>
          </div>
          <div className="list">
            {summaries.map((s) => (
              <button
                key={s.computer.id}
                disabled={s.state !== "ready"}
                onClick={() => {
                  setPicking(false);
                  nav.push({ name: "sessions", computerId: s.computer.id, newSession: true });
                }}
              >
                <span className="pick-name">{s.computer.label}</span>
                <span className="pick-state">{s.state === "ready" ? "Connected" : s.state === "unauthorized" ? "Removed this phone" : "Offline"}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </>
  );
}
